"use client";

import { useContext, useEffect, useState } from "react";
import Link from "next/link";
import { UserSubscriptionPlan } from "@/types/index";

import { SubscriptionPlan } from "@/types/index";
import {
  pricingData,
  PRO_EXTRA_BENEFITS,
  QUANT_EXTRA_BENEFITS,
  QUANT_INTRO_OFFER,
  isQuantIntroActive,
} from "@/config/subscriptions";
import { cn } from "@/lib/utils";
import { Button, buttonVariants } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { BillingFormButton } from "@/components/forms/billing-form-button";
import { ModalContext } from "@/components/modals/providers";
import {
  CheckChip,
  FeatureText,
  metalText,
  QuantCountdown,
  QuantQuarterlyToggle,
  QuantSeatsMeter,
} from "@/components/pricing/pricing-card-bits";
import { HeaderSection } from "@/components/shared/header-section";
import MaxWidthWrapper from "@/components/shared/max-width-wrapper";

interface PricingCardsProps {
  userId?: string;
  subscriptionPlan?: UserSubscriptionPlan;
  title?: string;
  subtitle?: string;
}

// Hidden for now per launch decision; flip back to true to show the free
// Sandbox card again.
const SHOW_SANDBOX_CARD = false;

export function PricingCards({ userId, subscriptionPlan, title, subtitle }: PricingCardsProps) {
  const [isYearly, setIsYearly] = useState<boolean>(false);
  // Quarterly billing exists only on the Quant plan, so it's an in-card
  // opt-in rather than another global toggle.
  const [quantQuarterly, setQuantQuarterly] = useState<boolean>(false);
  const [quantSeatsLeft, setQuantSeatsLeft] = useState<number | null>(null);
  const { setShowSignInModal } = useContext(ModalContext);

  useEffect(() => {
    fetch("/api/seats-left?plan=quant")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data && typeof data.seatsLeft === "number") setQuantSeatsLeft(data.seatsLeft);
      })
      .catch(() => {});
  }, []);

  const isReturningSubscriber = !!(subscriptionPlan && (subscriptionPlan.isPaid || subscriptionPlan.isCanceled));

  const toggleBilling = (value: string) => {
    if (value === "yearly" && !isYearly) {
      setIsYearly(true);
    } else if (value === "monthly" && isYearly) {
      setIsYearly(false);
    }
  };

  const freePlan = pricingData.find((p) => p.title === "Free")!;
  const gexPlan = pricingData.find((p) => p.title === "QuantWheel GEX")!;
  const proPlan = pricingData.find((p) => p.title === "QuantWheel PRO")!;
  const quantPlan = pricingData.find((p) => p.title === "QuantWheel Quant")!;

  const SandboxCard = () => (
    <div className="relative flex flex-col rounded-3xl border bg-background p-7 shadow-sm transition-shadow duration-200 hover:shadow-md">
      <p className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
        Sandbox (Free)
      </p>
      <div className="mt-6 flex items-baseline gap-1.5">
        <span className="text-4xl font-semibold tracking-tight">$0</span>
        <span className="text-sm text-muted-foreground">forever</span>
      </div>
      <p className="mt-1.5 text-xs text-muted-foreground">No signup or card required</p>
      <p className="mt-7 border-t pt-5 text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
        What&apos;s included
      </p>
      <ul className="mt-3 space-y-2.5 text-left text-sm">
        {freePlan.benefits.map((feature) => (
          <li className="flex gap-2.5" key={feature}>
            <CheckChip />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
      <div className="mt-auto pt-8">
        <Link
          href="/dashboard/wheel"
          className={cn(
            buttonVariants({ variant: "outline", rounded: "full", size: "lg" }),
            "w-full shadow-sm hover:shadow",
          )}
        >
          Launch Sandbox
        </Link>
      </div>
    </div>
  );

  const renderCta = (offer: SubscriptionPlan, highlight: boolean, useQuarterly: boolean) => {
    if (userId) {
      return (
        <BillingFormButton
          year={isYearly}
          interval={useQuarterly ? "quarterly" : undefined}
          offer={offer}
          subscriptionPlan={
            subscriptionPlan ||
            ({
              title: "Free",
              description: "Free plan",
              benefits: [],
              limitations: [],
              prices: { monthly: 0, yearly: 0 },
              stripeIds: { monthly: null, yearly: null },
              isPaid: false,
              isCanceled: false,
              stripeCurrentPeriodEnd: 0,
              stripeCustomerId: null,
              stripePriceId: null,
              stripeSubscriptionId: null,
              interval: null,
            } as UserSubscriptionPlan)
          }
          shouldStartTrial={!isReturningSubscriber}
        />
      );
    }
    return (
      <Button
        variant={highlight ? "default" : "outline"}
        size="lg"
        rounded="full"
        className={cn(
          "w-full shadow-sm hover:shadow",
          highlight &&
            "border-0 bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:opacity-90",
        )}
        onClick={() => {
          if (typeof window !== "undefined") {
            const billingCycle = useQuarterly ? "quarterly" : isYearly ? "yearly" : "monthly";
            sessionStorage.setItem("trialIntent", `${offer.title.toLowerCase().replace("quantwheel ", "")}|${billingCycle}`);
          }
          setShowSignInModal(true);
        }}
      >
        Start 7 Days for $1
      </Button>
    );
  };

  const TrialLine = () =>
    isReturningSubscriber ? null : (
      <p className="mt-1 text-xs">
        <span className="font-medium text-amber-600 dark:text-amber-400">$1 for 7 days</span>{" "}
        <span className="text-muted-foreground">to start. Cancel anytime.</span>
      </p>
    );

  // GEX and PRO share the clean, flat card; Quant gets the featured banner card.
  const StandardCard = ({ offer }: { offer: SubscriptionPlan }) => {
    const isPro = offer.title === "QuantWheel PRO";
    const yearlySavings = offer.prices.monthly * 12 - offer.prices.yearly;
    const displayedMonthly = isYearly ? Math.floor(offer.prices.yearly / 12) : offer.prices.monthly;

    return (
      <div
        className={cn(
          "relative flex flex-col rounded-3xl border bg-background p-7 text-left shadow-sm transition-shadow duration-200 hover:shadow-md",
          isPro && "border-2 border-primary/60",
        )}
      >
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
            QuantWheel{" "}
            <span className={isPro ? "text-primary" : "text-zinc-600 dark:text-zinc-300"}>
              {isPro ? "PRO" : "GEX"}
            </span>
          </p>
          {isPro && (
            <span className="rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-medium text-primary ring-1 ring-inset ring-primary/25">
              Most popular
            </span>
          )}
        </div>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          {isPro ? "For Wheel and Option Sellers" : "For directional traders"}
        </p>

        <div className="mt-6 flex items-baseline gap-1.5">
          {isYearly && (
            <span className="text-2xl tracking-tight text-muted-foreground/60 line-through">
              ${offer.prices.monthly}
            </span>
          )}
          <span className="text-4xl font-semibold tracking-tight">${displayedMonthly}</span>
          <span className="text-sm text-muted-foreground">/month</span>
        </div>
        <p className="mt-1.5 text-xs text-muted-foreground">
          {isYearly
            ? `$${offer.prices.yearly}/year billed annually - save $${yearlySavings}`
            : "Billed monthly. Cancel anytime."}
        </p>
        <TrialLine />

        <p className="mt-7 border-t pt-5 text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
          {isPro ? "Everything in GEX, plus" : "What's included"}
        </p>
        <ul className="mt-3 space-y-2.5 text-sm">
          {(isPro ? PRO_EXTRA_BENEFITS : offer.benefits).map((feature) => (
            <li className="flex gap-2.5" key={feature}>
              <CheckChip />
              <span><FeatureText text={feature} /></span>
            </li>
          ))}
        </ul>

        <div className="mt-auto pt-8">{renderCta(offer, isPro, false)}</div>
      </div>
    );
  };

  const QuantCard = ({ offer }: { offer: SubscriptionPlan }) => {
    const quantIntro = isQuantIntroActive();
    const quarterlyPrice = offer.prices.quarterly;
    const useQuarterly = quantQuarterly && !!quarterlyPrice;
    const yearlySavings = offer.prices.monthly * 12 - offer.prices.yearly;
    const displayedMonthly = useQuarterly
      ? Math.floor(quarterlyPrice! / 3)
      : isYearly
        ? Math.floor(offer.prices.yearly / 12)
        : offer.prices.monthly;

    const billingNote = useQuarterly ? (
      <>${quarterlyPrice} billed every 3 months</>
    ) : isYearly ? (
      <>
        {quantIntro && (
          <span className="text-white/40 line-through">
            ${QUANT_INTRO_OFFER.fullYearlyPrice}
          </span>
        )}{" "}
        ${offer.prices.yearly}/year billed annually - save ${yearlySavings}
      </>
    ) : quantIntro ? (
      <>Subscribe now and you&apos;re grandfathered at this price for as long as you stay subscribed.</>
    ) : (
      <>Billed monthly. Cancel anytime.</>
    );

    return (
      <div className="relative flex flex-col overflow-hidden rounded-3xl border border-zinc-300 text-left shadow-md dark:border-zinc-700">
        {quantIntro && <QuantCountdown />}
        {/* Dark graphite banner: liquid metal wordmark + launch offer, only here. */}
        <div className="relative bg-[#0d0e10] px-7 pb-6 pt-6">
          <div
            className="pointer-events-none absolute -right-16 -top-20 size-56 rounded-full bg-emerald-500/15 blur-3xl"
            aria-hidden="true"
          />
          <div className="relative z-10">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium uppercase tracking-wider text-zinc-400">
                QuantWheel <span style={metalText}>Quant</span>
              </p>
              {quantIntro && (
                <span className="whitespace-nowrap rounded-full bg-emerald-500/15 px-3 py-1 text-[11px] font-medium text-emerald-300 ring-1 ring-inset ring-emerald-400/25">
                  Limited to {QUANT_INTRO_OFFER.seatLimit} spots
                </span>
              )}
            </div>
            <p className="mt-1 text-xs leading-relaxed text-white/55">
              Flows, full API/MCP &amp; alpha access
            </p>

            <div className="mt-4 flex items-baseline gap-2">
              {quantIntro && (
                <span className="text-2xl tracking-tight text-white/40 line-through">
                  ${QUANT_INTRO_OFFER.fullMonthlyPrice}
                </span>
              )}
              <span className="text-5xl font-semibold tracking-tight text-white">
                ${displayedMonthly}
              </span>
              <span className="text-sm text-white/50">/month</span>
            </div>
            <p className="mt-1.5 text-xs leading-relaxed text-white/55">{billingNote}</p>

            {quarterlyPrice && (
              <QuantQuarterlyToggle
                className="mt-4"
                price={quarterlyPrice}
                selected={quantQuarterly}
                onToggle={() => setQuantQuarterly((prev) => !prev)}
              />
            )}

            {quantIntro && <QuantSeatsMeter seatsLeft={quantSeatsLeft} />}
          </div>
        </div>

        <div className="flex flex-1 flex-col bg-background px-7 pb-7 pt-5">
          <TrialLine />

          <p className="mt-4 border-t pt-4 text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
            Everything in PRO &amp; GEX, plus
          </p>
          <ul className="mt-3 space-y-2.5 text-sm">
            {QUANT_EXTRA_BENEFITS.map((feature) => (
              <li className="flex gap-2.5" key={feature}>
                <CheckChip />
                <span><FeatureText text={feature} /></span>
              </li>
            ))}
          </ul>

          <div className="mt-auto pt-8">{renderCta(offer, true, useQuarterly)}</div>
        </div>
      </div>
    );
  };

  return (
    <MaxWidthWrapper>
      <section id="pricing" className="flex flex-col items-center text-center py-32">
        <HeaderSection
          label="Pricing"
          title={title ?? "Choose the plan that's right for you"}
          subtitle={subtitle ?? "Try our sandbox for free, or start a trial. Cancel anytime."}
        />

        <div className="mb-6 mt-10 flex items-center gap-5">
          <ToggleGroup
            type="single"
            size="sm"
            value={isYearly ? "yearly" : "monthly"}
            onValueChange={toggleBilling}
            aria-label="toggle-year"
            className="h-auto sm:h-9 flex-col sm:flex-row overflow-hidden rounded-2xl sm:rounded-full border bg-background p-1 *:text-muted-foreground gap-1 sm:gap-0"
          >
            <ToggleGroupItem
              value="monthly"
              className="rounded-xl sm:rounded-full px-4 sm:px-5 py-2 sm:py-1 text-xs sm:text-sm data-[state=on]:!bg-primary data-[state=on]:!text-primary-foreground whitespace-nowrap"
              aria-label="Toggle monthly billing"
            >
              Monthly
            </ToggleGroupItem>
            <ToggleGroupItem
              value="yearly"
              className="rounded-xl sm:rounded-full px-4 sm:px-5 py-2 sm:py-1 text-xs sm:text-sm data-[state=on]:!bg-primary data-[state=on]:!text-primary-foreground whitespace-nowrap"
              aria-label="Toggle yearly billing"
            >
              Yearly (save up to 30%)
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        <div
          className={cn(
            "grid w-full items-stretch gap-5 bg-inherit py-5 max-w-7xl mx-auto",
            // All cards in a single row on laptop/desktop.
            SHOW_SANDBOX_CARD ? "md:grid-cols-2 lg:grid-cols-4" : "lg:grid-cols-3",
          )}
        >
          {SHOW_SANDBOX_CARD && <SandboxCard />}
          <StandardCard offer={gexPlan} />
          <StandardCard offer={proPlan} />
          <QuantCard offer={quantPlan} />
        </div>

        <Link
          href="/dashboard/wheel"
          className={cn(
            buttonVariants({ variant: "outline", size: "sm" }),
            "mt-6 text-xs text-muted-foreground",
          )}
        >
          Or try everything in Sandbox first
        </Link>

        <p className="mt-6 text-balance text-center text-base text-muted-foreground">
          Email{" "}
          <a
            className="font-medium text-primary hover:underline"
            href="mailto:hello@quantwheel.com"
          >
            hello@quantwheel.com
          </a>{" "}
          to contact our support team.
        </p>
      </section>
    </MaxWidthWrapper>
  );
}
